import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, EyeIcon, Loader2Icon, MoreVerticalIcon } from 'lucide-react';
import { PillButton } from '../../ui/PillButton';
import { Segmented } from '../../ui/Segmented';
import { ColorCheckbox } from '../../ui/ColorCheckbox';
import { CutoutFrame } from '../CutoutFrame';
type Format = 'stl' | 'obj' | '3mf';
type Status = 'idle' | 'exporting' | 'done';
const menu = ['Duplicate', 'Rename', 'Export Build File…', 'Export Mesh…'] as const;

/** The model ⋮ menu and a trimmed Export Mesh dialog. Exporting is simulated here. */
export function ExportDemo() {
  const [names, setNames] = useState(['Mini Chassis Kit']);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [format, setFormat] = useState<Format>('3mf');
  const [orientation, setOrientation] = useState(true);
  const [textures, setTextures] = useState(true);
  const [status, setStatus] = useState<Status>('idle');
  const timer = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  const pick = (item: (typeof menu)[number]) => {
    setMenuOpen(false);
    if (item === 'Duplicate') setNames(n => n.length < 3 ? [...n, `${n[0]} (copy)`] : n);
    if (item === 'Rename') setRenaming(true);
    if (item === 'Export Mesh…') {
      setDialog(true);
      setStatus('idle');
    }
  };
  const runExport = () => {
    setStatus('exporting');
    timer.current = window.setTimeout(() => setStatus('done'), 900);
  };
  return <CutoutFrame label="Model menu and Export Mesh dialog" className="pointer-events-auto bg-[#f2f6fb] p-[8px]" data-id="element-955" data-mp="2v7v0ci7jp8n0">
      <ul aria-label="Models" data-id="element-956" data-mp="2lc1m8ue6h734">
        {names.map((n, i) => <li key={i} className={`flex h-[26px] items-center gap-[6px] rounded-[4px] px-[6px] text-[11.5px] text-pf-ink ${i === 0 ? 'bg-[#d3e3f7] font-semibold' : ''}`} data-id="element-957" data-mp="2lcbhoxhzs6g7" data-mpi={i}>
            <EyeIcon className="h-[11px] w-[11px] shrink-0" strokeWidth={2.4} data-id="element-958" data-mp="jjn9e9qhqstk" />
            {i === 0 && renaming ? <input autoFocus aria-label="Model name" defaultValue={n} onFocus={e => e.target.select()} onBlur={e => {
          const v = e.target.value.trim();
          if (v) setNames(all => all.map((x, j) => j === 0 ? v : x));
          setRenaming(false);
        }} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()} className="h-[20px] min-w-0 flex-1 rounded-[3px] border border-pf-blue-line bg-white px-[4px] text-[11.5px] font-normal outline-none" data-id="element-959" data-mp="1sieo2f3th0e1" /> : <span className="min-w-0 flex-1 truncate" data-id="element-960" data-mp="22eztv2nnufix">{n}</span>}
            {i === 0 && <button type="button" aria-label="Model actions" aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)} className="flex h-[18px] w-[18px] items-center justify-center rounded-[4px] transition-colors duration-150 hover:bg-black/5" data-id="element-961" data-mp="1xhhua2vj9cnl">
            
                <MoreVerticalIcon className="h-[12px] w-[12px]" strokeWidth={2.4} data-id="element-962" data-mp="2xi36y7sr9i3m" />
              </button>}
          </li>)}
      </ul>
      {menuOpen && <ul role="menu" className="ml-auto mt-[2px] w-[150px] rounded-[6px] border border-pf-line bg-white py-[3px] shadow-[0_6px_16px_rgba(20,28,38,0.16)]" data-id="element-963" data-mp="37eg8tsygf29g">
          {menu.map(it => <li key={it} data-id="element-964" data-mp="3gfny5lizssre">
              <button role="menuitem" type="button" onClick={() => pick(it)} className="w-full px-[10px] py-[5px] text-left text-[11.5px] text-pf-ink transition-colors duration-150 hover:bg-pf-blue hover:text-white" data-id="element-965" data-mp="3gecurmtgln0t">
                {it}
              </button>
            </li>)}
        </ul>}
      {dialog && !menuOpen && <div className="mt-[8px] rounded-[7px] bg-white p-[10px] shadow-[0_3px_12px_rgba(20,28,38,0.14)] ring-1 ring-black/[0.06]" data-id="element-966" data-mp="3v6d8klomq3u7">
          <p className="text-[13px] font-bold text-pf-ink" data-id="element-967" data-mp="7zb5k6lozn48">Export Mesh</p>
          <p className="pb-[8px] text-[11px] text-pf-muted" data-id="element-968" data-mp="7x8851i7mrph">Without supports or print scaffolding.</p>
          <Segmented label="Export format" size="sm" value={format} onChange={f => {
        setFormat(f);
        setStatus('idle');
      }} options={[{
        value: 'stl',
        label: 'STL'
      }, {
        value: 'obj',
        label: 'OBJ'
      }, {
        value: '3mf',
        label: '3MF'
      }]} data-id="element-969" data-mp="3qfvmemkg20xe" />
        
          <div className="mt-[8px] flex flex-col gap-[4px] text-[12px] text-pf-ink" data-id="element-970" data-mp="37jw1fvpvvxnb">
            <label className="flex items-center gap-[8px]" data-id="element-971" data-mp="33itxx2oxozfj">
              <ColorCheckbox label="Current orientation" checked={orientation} onChange={setOrientation} data-id="element-972" data-mp="3hha6kz6cx00b" /> Current orientation
            </label>
            <label className={`flex items-center gap-[8px] ${format === 'stl' ? 'opacity-50' : ''}`} data-id="element-973" data-mp="3chp3qdo2g7ib">
              <ColorCheckbox label="Include textures" checked={format !== 'stl' && textures} onChange={v => format !== 'stl' && setTextures(v)} data-id="element-974" data-mp="wprj51bhcjwz" /> Include textures
            </label>
          </div>
          <div className="mt-[10px] flex items-center gap-[8px]" data-id="element-975" data-mp="29obanvl8fjsm">
            <span className="min-w-0 flex-1 truncate text-[11px] text-pf-muted" data-id="element-976" data-mp="2ep7p3337moca">
              {names[0].toLowerCase().replace(/\s+/g, '-')}.{format === 'obj' && textures ? 'zip' : format}
            </span>
            <PillButton variant="primary" className="h-[26px] w-[96px] flex-none" onClick={runExport} disabled={status === 'exporting'} data-id="element-977" data-mp="25nhph9z6cj3f">
              {status === 'exporting' && <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-978" data-mp="2xffll2cemgji" />}
              {status === 'done' && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-979" data-mp="2xh9xxs9m10bz" />}
              {status === 'exporting' ? 'Exporting…' : status === 'done' ? 'Exported' : 'Export'}
            </PillButton>
          </div>
        </div>}
    </CutoutFrame>;
}